import * as formulas from "./metricFormulas";
import {
  METRIC_KEY,
  MetricValues,
  MetricValueContext,
  MetricValueLevel,
  MetricValue,
} from "./metric";
import {
  PARAMETER_KEY,
  ParameterValues,
  ParameterValueContext,
  ParameterValue,
} from "./parameter";

/**
 * The result of a metric value calculation. If calculation fails, the value is undefined and
 * there is a corresponding message.
 */
export type MetricCalculationResult = {
  value?: number;
  context: MetricValueContext;
  messages: MetricCalculationResultMessage[];
};

/**
 * A message with information about the calculation
 */
export type MetricCalculationResultMessage = {
  message: string;
  severity: "info" | "warning" | "error";
};

/**
 * A function that calculates a new metric value for the given level
 * from other metric values and parameter values.
 */
type MetricCalculator = (
  metricValues: MetricValues,
  parameterValues: ParameterValues,
  metricValueLevel: MetricValueLevel
) => MetricCalculationResult;

export const popDec: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wPersHw = getMetricValue(
    METRIC_KEY.wPersHw,
    metricValues,
    metricValueLevel
  );

  const paramYearlyDecrease = getParameterValue(
    PARAMETER_KEY.paramYearlyDecrease,
    parameterValues,
    metricValueLevel
  );

  const paramObservPeriod = getParameterValue(
    PARAMETER_KEY.paramObservPeriod,
    parameterValues,
    metricValueLevel
  );

  if (
    wPersHw !== undefined &&
    paramYearlyDecrease !== undefined &&
    paramObservPeriod !== undefined
  ) {
    value = formulas.popDec(
      wPersHw.value,
      paramYearlyDecrease.value,
      paramObservPeriod.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(METRIC_KEY.popDec, metricValueLevel),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(METRIC_KEY.popDec, metricValueLevel),
    messages: messages,
  };
};

export const popInc: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wPersHw = getMetricValue(
    METRIC_KEY.wPersHw,
    metricValues,
    metricValueLevel
  );

  const paramYearlyDecrease = getParameterValue(
    PARAMETER_KEY.paramYearlyDecrease,
    parameterValues,
    metricValueLevel
  );
  const paramObservPeriod = getParameterValue(
    PARAMETER_KEY.paramObservPeriod,
    parameterValues,
    metricValueLevel
  );

  if (
    wPersHw !== undefined &&
    paramYearlyDecrease !== undefined &&
    paramObservPeriod !== undefined
  ) {
    value = formulas.popInc(
      wPersHw.value,
      paramYearlyDecrease.value,
      paramObservPeriod.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(METRIC_KEY.popInc, metricValueLevel),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(METRIC_KEY.popInc, metricValueLevel),
    messages: messages,
  };
};

export const popTh: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wPersHw = getMetricValue(
    METRIC_KEY.wPersHw,
    metricValues,
    metricValueLevel
  );
  const wohnflEinw = getMetricValue(
    METRIC_KEY.wohnflEinw,
    metricValues,
    metricValueLevel
  );

  const paramAreaPerResident = getParameterValue(
    PARAMETER_KEY.paramAreaPerResident,
    parameterValues,
    metricValueLevel
  );

  if (
    wPersHw !== undefined &&
    wohnflEinw !== undefined &&
    paramAreaPerResident !== undefined
  ) {
    value = formulas.popTh(
      wPersHw.value,
      wohnflEinw.value,
      paramAreaPerResident.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(METRIC_KEY.popTh, metricValueLevel),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(METRIC_KEY.popTh, metricValueLevel),
    messages: messages,
  };
};

export const wohnflEinw: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wohnflBest = getMetricValue(
    METRIC_KEY.wohnflBest,
    metricValues,
    metricValueLevel
  );

  const wPersHw = getMetricValue(
    METRIC_KEY.wPersHw,
    metricValues,
    metricValueLevel
  );

  if (wohnflBest !== undefined && wPersHw !== undefined) {
    value = formulas.wohnflEinw(wohnflBest.value, wPersHw.value);
  } else {
    messages.push({
      message: getMissingInputsMessage(METRIC_KEY.wohnflEinw, metricValueLevel),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(METRIC_KEY.wohnflEinw, metricValueLevel),
    messages: messages,
  };
};

export const populationVariation: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wPersHw = getMetricValue(
    METRIC_KEY.wPersHw,
    metricValues,
    metricValueLevel
  );
  const popDec = getMetricValue(
    METRIC_KEY.popDec,
    metricValues,
    metricValueLevel
  );
  const popInc = getMetricValue(
    METRIC_KEY.popInc,
    metricValues,
    metricValueLevel
  );
  const popTh = getMetricValue(
    METRIC_KEY.popTh,
    metricValues,
    metricValueLevel
  );

  if (
    wPersHw !== undefined &&
    popDec !== undefined &&
    popInc !== undefined &&
    popTh !== undefined
  ) {
    value = formulas.populationVariation(
      wPersHw.value,
      popDec.value,
      popInc.value,
      popTh.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationVariation,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationVariation,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationTemporary: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wohnWnartAndParamPairs = [
    [
      METRIC_KEY.wohnWnart3020Best,
      PARAMETER_KEY.paramPopulationPerApartment3020Best,
    ],
    [
      METRIC_KEY.wohnWnart3020ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3020ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3030Best,
      PARAMETER_KEY.paramPopulationPerApartment3030Best,
    ],
    [
      METRIC_KEY.wohnWnart3030ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3030ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3031Best,
      PARAMETER_KEY.paramPopulationPerApartment3031Best,
    ],
    [
      METRIC_KEY.wohnWnart3031ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3031ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3032Best,
      PARAMETER_KEY.paramPopulationPerApartment3032Best,
    ],
    [
      METRIC_KEY.wohnWnart3032ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3032ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3033Best,
      PARAMETER_KEY.paramPopulationPerApartment3033Best,
    ],
    [
      METRIC_KEY.wohnWnart3033ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3033ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3034Best,
      PARAMETER_KEY.paramPopulationPerApartment3034Best,
    ],
    [
      METRIC_KEY.wohnWnart3034ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3034ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3035Best,
      PARAMETER_KEY.paramPopulationPerApartment3035Best,
    ],
    [
      METRIC_KEY.wohnWnart3035ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3035ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3036Best,
      PARAMETER_KEY.paramPopulationPerApartment3036Best,
    ],
    [
      METRIC_KEY.wohnWnart3036ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3036ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3037Best,
      PARAMETER_KEY.paramPopulationPerApartment3037Best,
    ],
    [
      METRIC_KEY.wohnWnart3037ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3037ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3038Best,
      PARAMETER_KEY.paramPopulationPerApartment3038Best,
    ],
    [
      METRIC_KEY.wohnWnart3038ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3038ImbBew,
    ],
    [
      METRIC_KEY.wohnWnart3070Best,
      PARAMETER_KEY.paramPopulationPerApartment3070Best,
    ],
    [
      METRIC_KEY.wohnWnart3070ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3070ImbBew,
    ],
  ];

  const apartmentInfo: {
    wohnWnart: number;
    paramPopulationPerApartment: number;
  }[] = [];

  for (const wohnWnartAndParamPair of wohnWnartAndParamPairs) {
    const wohnWnartMetricValue = getMetricValue(
      wohnWnartAndParamPair[0],
      metricValues,
      metricValueLevel
    );

    const paramPopulationPerApartmentParameterValue = getParameterValue(
      wohnWnartAndParamPair[1],
      parameterValues,
      metricValueLevel
    );

    if (
      wohnWnartMetricValue !== undefined &&
      paramPopulationPerApartmentParameterValue !== undefined
    ) {
      apartmentInfo.push({
        wohnWnart: wohnWnartMetricValue.value,
        paramPopulationPerApartment:
          paramPopulationPerApartmentParameterValue.value,
      });
    } else {
      messages.push({
        message: getMissingInputsMessage(
          METRIC_KEY.populationTemporary,
          metricValueLevel
        ),
        severity: "error",
      });
    }
  }

  value = formulas.populationTemporary(apartmentInfo);

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationTemporary,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationPending: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wohnWnartAndParamPairs = [
    [
      METRIC_KEY.wohnWnart3010Best0E,
      PARAMETER_KEY.paramPopulationPerApartment3010Best,
    ],
    [
      METRIC_KEY.wohnWnart3010ImbBew,
      PARAMETER_KEY.paramPopulationPerApartment3010ImbBew,
    ],
    [
      METRIC_KEY.wohnWnartNullBest,
      PARAMETER_KEY.paramPopulationPerApartmentNullBest,
    ],
    [
      METRIC_KEY.wohnWnartNullImbBew,
      PARAMETER_KEY.paramPopulationPerApartmentNullImbBew,
    ],
  ];

  const apartmentInfo: {
    wohnWnart: number;
    paramPopulationPerApartment: number;
  }[] = [];

  for (const wohnWnartAndParamPair of wohnWnartAndParamPairs) {
    const wohnWnartMetricValue = getMetricValue(
      wohnWnartAndParamPair[0],
      metricValues,
      metricValueLevel
    );

    const paramPopulationPerApartmentParameterValue = getParameterValue(
      wohnWnartAndParamPair[1],
      parameterValues,
      metricValueLevel
    );

    if (
      wohnWnartMetricValue !== undefined &&
      paramPopulationPerApartmentParameterValue !== undefined
    ) {
      apartmentInfo.push({
        wohnWnart: wohnWnartMetricValue.value,
        paramPopulationPerApartment:
          paramPopulationPerApartmentParameterValue.value,
      });
    } else {
      messages.push({
        message: getMissingInputsMessage(
          METRIC_KEY.populationPending,
          metricValueLevel
        ),
        severity: "error",
      });
    }
  }

  value = formulas.populationPending(apartmentInfo);

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationPending,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationRelevant: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wPersHw = getMetricValue(
    METRIC_KEY.wPersHw,
    metricValues,
    metricValueLevel
  );

  const populationTemporary = getMetricValue(
    METRIC_KEY.populationTemporary,
    metricValues,
    metricValueLevel
  );

  const populationPending = getMetricValue(
    METRIC_KEY.populationPending,
    metricValues,
    metricValueLevel
  );

  if (
    wPersHw !== undefined &&
    populationTemporary !== undefined &&
    populationPending !== undefined
  ) {
    value = formulas.populationRelevant(
      wPersHw.value,
      populationTemporary.value,
      populationPending.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationRelevant,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationRelevant,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const areaRealizedCovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const wohnflBest = getMetricValue(
    METRIC_KEY.wohnflBest,
    metricValues,
    metricValueLevel
  );

  const wohnflImbBew = getMetricValue(
    METRIC_KEY.wohnflImbBew,
    metricValues,
    metricValueLevel
  );

  if (wohnflBest !== undefined && wohnflImbBew !== undefined) {
    value = formulas.areaRealizedCovered(wohnflBest.value, wohnflImbBew.value);
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.areaRealizedCovered,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.areaRealizedCovered,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationPotCovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const areaRealizedCovered = getMetricValue(
    METRIC_KEY.areaRealizedCovered,
    metricValues,
    metricValueLevel
  );

  const areaPotCovered = getMetricValue(
    METRIC_KEY.areaPotCovered,
    metricValues,
    metricValueLevel
  );

  const paramAreaPerResident = getParameterValue(
    PARAMETER_KEY.paramAreaPerResident,
    parameterValues,
    metricValueLevel
  );

  const paramDensificationRatioCovered = getParameterValue(
    PARAMETER_KEY.paramDensificationRatioCovered,
    parameterValues,
    metricValueLevel
  );

  if (
    areaRealizedCovered !== undefined &&
    areaPotCovered !== undefined &&
    paramAreaPerResident !== undefined &&
    paramDensificationRatioCovered !== undefined
  ) {
    value = formulas.populationPotCovered(
      areaPotCovered.value,
      areaRealizedCovered.value,
      paramAreaPerResident.value,
      paramDensificationRatioCovered.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationPotCovered,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationPotCovered,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationPotTotalCovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const populationPotCovered = getMetricValue(
    METRIC_KEY.populationPotCovered,
    metricValues,
    metricValueLevel
  );

  const populationRelevant = getMetricValue(
    METRIC_KEY.populationRelevant,
    metricValues,
    metricValueLevel
  );

  if (populationPotCovered !== undefined && populationRelevant !== undefined) {
    value = formulas.populationPotTotalCovered(
      populationPotCovered.value,
      populationRelevant.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationPotTotalCovered,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationPotTotalCovered,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationCapacityCovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const populationVariation = getMetricValue(
    METRIC_KEY.populationVariation,
    metricValues,
    metricValueLevel
  );

  const populationPotTotalCovered = getMetricValue(
    METRIC_KEY.populationPotTotalCovered,
    metricValues,
    metricValueLevel
  );

  if (
    populationVariation !== undefined &&
    populationPotTotalCovered !== undefined
  ) {
    value = formulas.populationCapacityCovered(
      populationPotTotalCovered.value,
      populationVariation.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationCapacityCovered,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationCapacityCovered,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const varHabitationRatioFinal: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;

  var wohnant = getMetricValue(
    METRIC_KEY.wohnant,
    metricValues,
    metricValueLevel
  );

  if (wohnant !== undefined && wohnant.value > 0) {
    value = wohnant.value;
  } else {
    value = getParameterValue(
      PARAMETER_KEY.paramHabitationRatio,
      parameterValues,
      metricValueLevel
    )?.value;
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.varHabitationRatioFinal,
      metricValueLevel
    ),
    messages: [],
  };
};

export const areaPotCovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  if (
    metricValueLevel.name === "districtZoneGde" &&
    metricValueLevel.zonTypPbg <= 3000
  ) {
    let value = undefined;
    let messages: MetricCalculationResultMessage[] = [];

    const az = getMetricValue(METRIC_KEY.az, metricValues, metricValueLevel);
    const uez = getMetricValue(METRIC_KEY.uez, metricValues, metricValueLevel);

    if (
      (uez !== undefined && uez.value !== 0) ||
      az === undefined ||
      az.value === 0
    ) {
      // Calculate with UEZ

      const varUezFinal = getMetricValue(
        METRIC_KEY.varUezFinal,
        metricValues,
        metricValueLevel
      );

      const fBebaut = getMetricValue(
        METRIC_KEY.fBebaut,
        metricValues,
        metricValueLevel
      );

      const avgBuildingHeightFinal = getMetricValue(
        METRIC_KEY.avgBuildingHeightFinal,
        metricValues,
        metricValueLevel
      );

      const paramUezRatio = getParameterValue(
        PARAMETER_KEY.paramUezRatio,
        parameterValues,
        metricValueLevel
      );

      const varHabitationRatioFinal = getMetricValue(
        METRIC_KEY.varHabitationRatioFinal,
        metricValues,
        metricValueLevel
      );

      const paramDevelopRatioCovered = getParameterValue(
        PARAMETER_KEY.paramDevelopRatioCovered,
        parameterValues,
        metricValueLevel
      );

      if (
        fBebaut !== undefined &&
        avgBuildingHeightFinal !== undefined &&
        avgBuildingHeightFinal.value !== 0 &&
        varUezFinal !== undefined &&
        varUezFinal.value !== 0 &&
        paramUezRatio !== undefined &&
        varHabitationRatioFinal !== undefined &&
        paramDevelopRatioCovered !== undefined
      ) {
        messages.push({
          message: getCalculateWithUezMessage(
            METRIC_KEY.areaPotCovered,
            metricValueLevel
          ),
          severity: "info",
        });

        value = formulas.areaPotCoveredUez(
          fBebaut.value,
          avgBuildingHeightFinal.value,
          varUezFinal.value,
          paramUezRatio.value,
          varHabitationRatioFinal.value,
          paramDevelopRatioCovered.value
        );

        return {
          value: value,
          context: new MetricValueContext(
            METRIC_KEY.areaPotCovered,
            metricValueLevel
          ),
          messages: messages,
        };
      } else {
        messages.push({
          message: getMissingInputsMessage(
            METRIC_KEY.areaPotCovered,
            metricValueLevel
          ),
          severity: "error",
        });
      }
    } else {
      // Calculate with AZ

      const fBebaut = getMetricValue(
        METRIC_KEY.fBebaut,
        metricValues,
        metricValueLevel
      );

      const varHabitationRatioFinal = getMetricValue(
        METRIC_KEY.varHabitationRatioFinal,
        metricValues,
        metricValueLevel
      );

      const paramDevelopRatioCovered = getParameterValue(
        PARAMETER_KEY.paramDevelopRatioCovered,
        parameterValues,
        metricValueLevel
      );

      if (
        fBebaut !== undefined &&
        az !== undefined &&
        az.value !== 0 &&
        varHabitationRatioFinal !== undefined &&
        paramDevelopRatioCovered !== undefined
      ) {
        messages.push({
          message: getCalculateWithAzMessage(
            METRIC_KEY.areaPotCovered,
            metricValueLevel
          ),
          severity: "info",
        });

        value = formulas.areaPotCoveredAz(
          fBebaut.value,
          az.value,
          varHabitationRatioFinal.value,
          paramDevelopRatioCovered.value
        );

        return {
          value: value,
          context: new MetricValueContext(
            METRIC_KEY.areaPotCovered,
            metricValueLevel
          ),
          messages: messages,
        };
      } else {
        messages.push({
          message: getMissingInputsMessage(
            METRIC_KEY.areaPotCovered,
            metricValueLevel
          ),
          severity: "error",
        });
      }
    }
  }

  return {
    value: 0,
    context: new MetricValueContext(
      METRIC_KEY.areaPotCovered,
      metricValueLevel
    ),
    messages: [
      {
        message: getSkipPbgCalculationMessage(
          METRIC_KEY.areaPotCovered,
          metricValueLevel
        ),
        severity: "info",
      },
    ],
  };
};

export const varUezFinal: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  if (
    metricValueLevel.name === "districtZoneGde" &&
    metricValueLevel.zonTypPbg <= 3000
  ) {
    let value = undefined;
    let messages: MetricCalculationResultMessage[] = [];

    const uez = getMetricValue(METRIC_KEY.uez, metricValues, metricValueLevel);

    const paramUez = getParameterValue(
      PARAMETER_KEY.paramUez,
      parameterValues,
      metricValueLevel
    );

    value =
      uez !== undefined && uez.value !== 0
        ? uez.value
        : paramUez !== undefined && paramUez.value !== 0
        ? paramUez.value
        : undefined;

    if (value === undefined) {
      messages.push({
        message: getMissingInputsMessage(
          METRIC_KEY.varUezFinal,
          metricValueLevel
        ),
        severity: "error",
      });
    }

    return {
      value: value,
      context: new MetricValueContext(METRIC_KEY.varUezFinal, metricValueLevel),
      messages: messages,
    };
  }
  return {
    value: 0,
    context: new MetricValueContext(METRIC_KEY.varUezFinal, metricValueLevel),
    messages: [
      {
        message: getSkipPbgCalculationMessage(
          METRIC_KEY.varUezFinal,
          metricValueLevel
        ),
        severity: "info",
      },
    ],
  };
};

export const avgBuildingHeight: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const fahoeMax = getMetricValue(
    METRIC_KEY.fahoeMax,
    metricValues,
    metricValueLevel
  );

  const geshoe = getMetricValue(
    METRIC_KEY.geshoe,
    metricValues,
    metricValueLevel
  );

  if (fahoeMax !== undefined && geshoe !== undefined) {
    value = formulas.avgBuildingHeight(fahoeMax.value, geshoe.value);
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.avgBuildingHeight,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.avgBuildingHeight,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const avgBuildingHeightFinal: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  if (
    metricValueLevel.name === "districtZoneGde" &&
    metricValueLevel.zonTypPbg <= 3000
  ) {
    let messages: MetricCalculationResultMessage[] = [];

    const avgBuildingHeight = getMetricValue(
      METRIC_KEY.avgBuildingHeight,
      metricValues,
      metricValueLevel
    );

    const paramBuildingHeight = getParameterValue(
      PARAMETER_KEY.paramBuildingHeight,
      parameterValues,
      metricValueLevel
    );

    const value =
      avgBuildingHeight !== undefined && avgBuildingHeight.value !== 0
        ? avgBuildingHeight.value
        : paramBuildingHeight !== undefined
        ? paramBuildingHeight.value
        : undefined;

    if (value === undefined) {
      messages.push({
        message: getMissingInputsMessage(
          METRIC_KEY.avgBuildingHeightFinal,
          metricValueLevel
        ),
        severity: "error",
      });
    }

    return {
      value: value,
      context: new MetricValueContext(
        METRIC_KEY.avgBuildingHeightFinal,
        metricValueLevel
      ),
      messages: messages,
    };
  }

  return {
    value: 0,
    context: new MetricValueContext(
      METRIC_KEY.avgBuildingHeightFinal,
      metricValueLevel
    ),
    messages: [
      {
        message: getSkipPbgCalculationMessage(
          METRIC_KEY.avgBuildingHeightFinal,
          metricValueLevel
        ),
        severity: "info",
      },
    ],
  };
};

export const populationCapacity: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const populationCapacityCovered = getMetricValue(
    METRIC_KEY.populationCapacityCovered,
    metricValues,
    metricValueLevel
  );

  const populationCapacityUncovered = getMetricValue(
    METRIC_KEY.populationCapacityUncovered,
    metricValues,
    metricValueLevel
  );

  if (
    populationCapacityCovered !== undefined &&
    populationCapacityUncovered !== undefined
  ) {
    value = formulas.populationCapacity(
      populationCapacityCovered.value,
      populationCapacityUncovered.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationCapacity,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationCapacity,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const areaPotUncovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  if (
    metricValueLevel.name === "districtZoneGde" &&
    metricValueLevel.zonTypPbg <= 3000
  ) {
    let value = undefined;
    let messages: MetricCalculationResultMessage[] = [];

    const az = getMetricValue(METRIC_KEY.az, metricValues, metricValueLevel);
    const uez = getMetricValue(METRIC_KEY.uez, metricValues, metricValueLevel);

    if (
      (uez !== undefined && uez.value !== 0) ||
      az === undefined ||
      az.value === 0
    ) {
      // Calculate with UEZ
      const fUnbebaut = getMetricValue(
        METRIC_KEY.fUnbebaut,
        metricValues,
        metricValueLevel
      );

      const avgBuildingHeightFinal = getMetricValue(
        METRIC_KEY.avgBuildingHeightFinal,
        metricValues,
        metricValueLevel
      );

      const varUezFinal = getMetricValue(
        METRIC_KEY.varUezFinal,
        metricValues,
        metricValueLevel
      );

      const paramUezRatio = getParameterValue(
        PARAMETER_KEY.paramUezRatio,
        parameterValues,
        metricValueLevel
      );

      const varHabitationRatioFinal = getMetricValue(
        METRIC_KEY.varHabitationRatioFinal,
        metricValues,
        metricValueLevel
      );

      const paramDevelopRatioUncovered = getParameterValue(
        PARAMETER_KEY.paramDevelopRatioUncovered,
        parameterValues,
        metricValueLevel
      );

      if (
        fUnbebaut !== undefined &&
        avgBuildingHeightFinal !== undefined &&
        avgBuildingHeightFinal.value !== 0 &&
        varUezFinal !== undefined &&
        varUezFinal.value !== 0 &&
        paramUezRatio !== undefined &&
        varHabitationRatioFinal !== undefined &&
        paramDevelopRatioUncovered !== undefined
      ) {
        messages.push({
          message: getCalculateWithUezMessage(
            METRIC_KEY.areaPotUncovered,
            metricValueLevel
          ),
          severity: "info",
        });

        value = formulas.areaPotUncoveredUez(
          fUnbebaut.value,
          avgBuildingHeightFinal.value,
          varUezFinal.value,
          paramUezRatio.value,
          varHabitationRatioFinal.value,
          paramDevelopRatioUncovered.value
        );

        return {
          value: value,
          context: new MetricValueContext(
            METRIC_KEY.areaPotUncovered,
            metricValueLevel
          ),
          messages: messages,
        };
      } else {
        messages.push({
          message: getMissingInputsMessage(
            METRIC_KEY.areaPotUncovered,
            metricValueLevel
          ),
          severity: "error",
        });
      }
    } else {
      // Calculate with AZ

      const fUnbebaut = getMetricValue(
        METRIC_KEY.fUnbebaut,
        metricValues,
        metricValueLevel
      );

      const varHabitationRatioFinal = getMetricValue(
        METRIC_KEY.varHabitationRatioFinal,
        metricValues,
        metricValueLevel
      );

      const paramDevelopRatioUnCovered = getParameterValue(
        PARAMETER_KEY.paramDevelopRatioUncovered,
        parameterValues,
        metricValueLevel
      );

      if (
        fUnbebaut !== undefined &&
        az !== undefined &&
        az.value !== 0 &&
        varHabitationRatioFinal !== undefined &&
        paramDevelopRatioUnCovered !== undefined
      ) {
        messages.push({
          message: getCalculateWithAzMessage(
            METRIC_KEY.areaPotUncovered,
            metricValueLevel
          ),
          severity: "info",
        });

        value = formulas.areaPotUncoveredAz(
          fUnbebaut.value,
          az.value,
          varHabitationRatioFinal.value,
          paramDevelopRatioUnCovered.value
        );
      } else {
        messages.push({
          message: getMissingInputsMessage(
            METRIC_KEY.areaPotUncovered,
            metricValueLevel
          ),
          severity: "error",
        });
      }

      return {
        value: value,
        context: new MetricValueContext(
          METRIC_KEY.areaPotUncovered,
          metricValueLevel
        ),
        messages: messages,
      };
    }
  }

  return {
    value: 0,
    context: new MetricValueContext(
      METRIC_KEY.areaPotUncovered,
      metricValueLevel
    ),
    messages: [
      {
        message: getSkipPbgCalculationMessage(
          METRIC_KEY.areaPotUncovered,
          metricValueLevel
        ),
        severity: "info",
      },
    ],
  };
};

export const populationPotUncovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  if (
    metricValueLevel.name === "districtZoneGde" &&
    metricValueLevel.zonTypPbg <= 3000
  ) {
    let value = undefined;
    let messages: MetricCalculationResultMessage[] = [];

    const areaPotUncovered = getMetricValue(
      METRIC_KEY.areaPotUncovered,
      metricValues,
      metricValueLevel
    );

    const paramAreaPerResident = getParameterValue(
      PARAMETER_KEY.paramAreaPerResident,
      parameterValues,
      metricValueLevel
    );

    if (areaPotUncovered !== undefined && paramAreaPerResident !== undefined) {
      value = formulas.populationPotUncovered(
        areaPotUncovered.value,
        paramAreaPerResident.value
      );
    } else {
      messages.push({
        message: getMissingInputsMessage(
          METRIC_KEY.populationPotUncovered,
          metricValueLevel
        ),
        severity: "error",
      });
    }

    return {
      value: value,
      context: new MetricValueContext(
        METRIC_KEY.populationPotUncovered,
        metricValueLevel
      ),
      messages: messages,
    };
  }

  return {
    value: 0,
    context: new MetricValueContext(
      METRIC_KEY.populationPotUncovered,
      metricValueLevel
    ),
    messages: [
      {
        message: getSkipPbgCalculationMessage(
          METRIC_KEY.populationPotUncovered,
          metricValueLevel
        ),
        severity: "info",
      },
    ],
  };
};

export const populationCapacityUncovered: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  if (
    metricValueLevel.name === "districtZoneGde" &&
    metricValueLevel.zonTypPbg <= 3000
  ) {
    let value = undefined;
    let messages: MetricCalculationResultMessage[] = [];

    const populationPotUncovered = getMetricValue(
      METRIC_KEY.populationPotUncovered,
      metricValues,
      metricValueLevel
    );

    const paramDensificationRatioUncovered = getParameterValue(
      PARAMETER_KEY.paramDensificationRatioUncovered,
      parameterValues,
      metricValueLevel
    );

    if (
      populationPotUncovered !== undefined &&
      paramDensificationRatioUncovered !== undefined
    ) {
      value = formulas.populationCapacityUncovered(
        populationPotUncovered.value,
        paramDensificationRatioUncovered.value
      );
    } else {
      messages.push({
        message: getMissingInputsMessage(
          METRIC_KEY.populationCapacityUncovered,
          metricValueLevel
        ),
        severity: "error",
      });
    }

    return {
      value: value,
      context: new MetricValueContext(
        METRIC_KEY.populationCapacityUncovered,
        metricValueLevel
      ),
      messages: messages,
    };
  }

  return {
    value: 0,
    context: new MetricValueContext(
      METRIC_KEY.populationCapacityUncovered,
      metricValueLevel
    ),
    messages: [
      {
        message: getSkipPbgCalculationMessage(
          METRIC_KEY.populationCapacityUncovered,
          metricValueLevel
        ),
        severity: "info",
      },
    ],
  };
};

export const lBuildingAreaDemandActual: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];
  let metricValueLevelLocal: MetricValueLevel;

  if (metricValueLevel.name === "district") {
    metricValueLevelLocal = {
      name: "districtResidential",
      bfsNr: metricValueLevel.bfsNr,
      bfsNr1999: metricValueLevel.bfsNr1999,
    };
  } else {
    metricValueLevelLocal = metricValueLevel;
  }

  const fBebaut = getMetricValue(
    METRIC_KEY.fBebaut,
    metricValues,
    metricValueLevelLocal
  );

  const populationRelevant = getMetricValue(
    METRIC_KEY.populationRelevant,
    metricValues,
    metricValueLevelLocal
  );

  if (fBebaut !== undefined && populationRelevant !== undefined) {
    value = formulas.lBuildingAreaDemandActual(
      fBebaut.value,
      populationRelevant.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.lBuildingAreaDemandActual,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.lBuildingAreaDemandActual,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const mBuildingAreaDemandExpected: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];
  let metricValueLevelLocal: MetricValueLevel;

  if (metricValueLevel.name === "district") {
    metricValueLevelLocal = {
      name: "districtResidential",
      bfsNr: metricValueLevel.bfsNr,
      bfsNr1999: metricValueLevel.bfsNr1999,
    };
  } else {
    metricValueLevelLocal = metricValueLevel;
  }

  const fTotal = getMetricValue(
    METRIC_KEY.fTotal,
    metricValues,
    metricValueLevelLocal
  );

  const populationCapacity = getMetricValue(
    METRIC_KEY.populationCapacity,
    metricValues,
    metricValueLevelLocal
  );

  if (fTotal !== undefined && populationCapacity !== undefined) {
    value = formulas.mBuildingAreaDemandExpected(
      fTotal.value,
      populationCapacity.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.mBuildingAreaDemandExpected,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.mBuildingAreaDemandExpected,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const bPopulationCapacityTotal: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const populationCapacity = getMetricValue(
    METRIC_KEY.populationCapacity,
    metricValues,
    metricValueLevel
  );

  const populationCorrection = getMetricValue(
    METRIC_KEY.populationCorrection,
    metricValues,
    metricValueLevel
  );

  const populationTemporary = getMetricValue(
    METRIC_KEY.populationTemporary,
    metricValues,
    metricValueLevel
  );

  if (
    populationCapacity !== undefined &&
    populationCorrection !== undefined &&
    populationTemporary !== undefined
  ) {
    value = formulas.bPopulationCapacityTotal(
      populationCapacity.value,
      populationCorrection.value,
      populationTemporary.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.bPopulationCapacityTotal,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.bPopulationCapacityTotal,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const cPopulationGrowth: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const bPopulationCapacityTotal = getMetricValue(
    METRIC_KEY.bPopulationCapacityTotal,
    metricValues,
    metricValueLevel
  );

  const populationKgwrFinal = getMetricValue(
    METRIC_KEY.populationKgwrFinal,
    metricValues,
    metricValueLevel
  );

  if (
    bPopulationCapacityTotal !== undefined &&
    populationKgwrFinal !== undefined
  ) {
    value = formulas.cPopulationGrowth(
      bPopulationCapacityTotal.value,
      populationKgwrFinal.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.cPopulationGrowth,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.cPopulationGrowth,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const ePopulationGrowthAnnual: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const cPopulationGrowth = getMetricValue(
    METRIC_KEY.cPopulationGrowth,
    metricValues,
    metricValueLevel
  );

  const paramObservPeriod = getParameterValue(
    PARAMETER_KEY.paramObservPeriod,
    parameterValues,
    metricValueLevel
  );

  if (cPopulationGrowth !== undefined && paramObservPeriod !== undefined) {
    value = formulas.ePopulationGrowthAnnual(
      cPopulationGrowth.value,
      paramObservPeriod.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.ePopulationGrowthAnnual,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.ePopulationGrowthAnnual,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const rPopulationNewZonesDemand: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const bPopulationCapacityTotal = getMetricValue(
    METRIC_KEY.bPopulationCapacityTotal,
    metricValues,
    metricValueLevel
  );

  const qPopulationFuture = getMetricValue(
    METRIC_KEY.qPopulationFuture,
    metricValues,
    metricValueLevel
  );

  if (
    bPopulationCapacityTotal !== undefined &&
    qPopulationFuture !== undefined
  ) {
    value = formulas.rPopulationNewZonesDemand(
      bPopulationCapacityTotal.value,
      qPopulationFuture.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.rPopulationNewZonesDemand,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.rPopulationNewZonesDemand,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const qPopulationFuture: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const pVarFactorRp = getMetricValue(
    METRIC_KEY.pVarFactorRp,
    metricValues,
    metricValueLevel
  );

  const populationHist = getMetricValue(
    METRIC_KEY.populationHist,
    metricValues,
    metricValueLevel
  );

  const paramRelevantYearPopulation = getParameterValue(
    PARAMETER_KEY.paramRelevantYearPopulation,
    parameterValues,
    metricValueLevel
  );

  const paramObservPeriod = getParameterValue(
    PARAMETER_KEY.paramObservPeriod,
    parameterValues,
    metricValueLevel
  );

  if (
    pVarFactorRp !== undefined &&
    populationHist !== undefined &&
    paramRelevantYearPopulation !== undefined &&
    paramObservPeriod !== undefined
  ) {
    value = formulas.qPopulationFuture(
      populationHist.value,
      pVarFactorRp.value,
      paramObservPeriod.value,
      paramRelevantYearPopulation.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.qPopulationFuture,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.qPopulationFuture,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const dPopulationGrowthPercent: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const cPopulationGrowth = getMetricValue(
    METRIC_KEY.cPopulationGrowth,
    metricValues,
    metricValueLevel
  );
  const populationKgwrFinal = getMetricValue(
    METRIC_KEY.populationKgwrFinal,
    metricValues,
    metricValueLevel
  );

  if (cPopulationGrowth !== undefined && populationKgwrFinal !== undefined) {
    value = formulas.dPopulationGrowthPercent(
      cPopulationGrowth.value,
      populationKgwrFinal.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.dPopulationGrowthPercent,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.dPopulationGrowthPercent,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const fPopulationGrowthAnnualPercent: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const bPopulationCapacityTotal = getMetricValue(
    METRIC_KEY.bPopulationCapacityTotal,
    metricValues,
    metricValueLevel
  );
  const populationKgwrFinal = getMetricValue(
    METRIC_KEY.populationKgwrFinal,
    metricValues,
    metricValueLevel
  );

  const paramObservPeriod = getParameterValue(
    PARAMETER_KEY.paramObservPeriod,
    parameterValues,
    metricValueLevel
  );

  if (
    bPopulationCapacityTotal !== undefined &&
    populationKgwrFinal !== undefined &&
    paramObservPeriod !== undefined
  ) {
    value = formulas.fPopulationGrowthAnnualPercent(
      bPopulationCapacityTotal.value,
      populationKgwrFinal.value,
      paramObservPeriod.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.fPopulationGrowthAnnualPercent,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.fPopulationGrowthAnnualPercent,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationKgwrFinal: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const aPopulationActual = getMetricValue(
    METRIC_KEY.aPopulationActual,
    metricValues,
    metricValueLevel
  );

  const populationHist = getMetricValue(
    METRIC_KEY.populationHist,
    metricValues,
    metricValueLevel
  );

  if (aPopulationActual !== undefined && populationHist !== undefined) {
    value = formulas.populationKgwrFinal(
      aPopulationActual.value,
      populationHist.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationKgwrFinal,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationKgwrFinal,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationCorrection: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const populationKgwrFinal = getMetricValue(
    METRIC_KEY.populationKgwrFinal,
    metricValues,
    metricValueLevel
  );
  const aPopulationActual = getMetricValue(
    METRIC_KEY.aPopulationActual,
    metricValues,
    metricValueLevel
  );

  if (populationKgwrFinal !== undefined && aPopulationActual !== undefined) {
    value = formulas.populationCorrection(
      populationKgwrFinal.value,
      aPopulationActual.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationCorrection,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(
      METRIC_KEY.populationCorrection,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const oBuildingAreaDemandComparison: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let messages: MetricCalculationResultMessage[] = [];
  let metricValueLevelLocal: MetricValueLevel;

  if (metricValueLevel.name === "district") {
    metricValueLevelLocal = {
      name: "districtResidential",
      bfsNr: metricValueLevel.bfsNr,
      bfsNr1999: metricValueLevel.bfsNr1999,
    };
  } else {
    metricValueLevelLocal = metricValueLevel;
  }

  const paramBuildingAreaDemand = getParameterValue(
    PARAMETER_KEY.paramBuildingAreaDemand,
    parameterValues,
    metricValueLevelLocal
  );

  if (paramBuildingAreaDemand === undefined) {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.oBuildingAreaDemandComparison,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: paramBuildingAreaDemand?.value,
    context: new MetricValueContext(
      METRIC_KEY.oBuildingAreaDemandComparison,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const pVarFactorRp: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let messages: MetricCalculationResultMessage[] = [];

  const paramFactorRp = getParameterValue(
    PARAMETER_KEY.paramFactorRp,
    parameterValues,
    metricValueLevel
  );

  if (paramFactorRp === undefined) {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.pVarFactorRp,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: paramFactorRp?.value,
    context: new MetricValueContext(METRIC_KEY.pVarFactorRp, metricValueLevel),
    messages: messages,
  };
};

export const aPopulationActual: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let messages: MetricCalculationResultMessage[] = [];

  const aParamPopulationActual = getParameterValue(
    PARAMETER_KEY.aParamPopulationActual,
    parameterValues,
    metricValueLevel
  );

  if (aParamPopulationActual === undefined) {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.aPopulationActual,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: aParamPopulationActual?.value,
    context: new MetricValueContext(
      METRIC_KEY.aPopulationActual,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const populationHist: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let messages: MetricCalculationResultMessage[] = [];

  const paramPopulationHist = getParameterValue(
    PARAMETER_KEY.paramPopulationHist,
    parameterValues,
    metricValueLevel
  );

  if (paramPopulationHist === undefined) {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.populationHist,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: paramPopulationHist?.value,
    context: new MetricValueContext(
      METRIC_KEY.populationHist,
      metricValueLevel
    ),
    messages: messages,
  };
};

export const compensation: MetricCalculator = (
  metricValues,
  parameterValues,
  metricValueLevel
) => {
  let value = undefined;
  let messages: MetricCalculationResultMessage[] = [];

  const mBuildingAreaDemandExpected = getMetricValue(
    METRIC_KEY.mBuildingAreaDemandExpected,
    metricValues,
    metricValueLevel
  );

  const oBuildingAreaDemandComparison = getMetricValue(
    METRIC_KEY.oBuildingAreaDemandComparison,
    metricValues,
    metricValueLevel
  );

  const rPopulationNewZonesDemand = getMetricValue(
    METRIC_KEY.rPopulationNewZonesDemand,
    metricValues,
    metricValueLevel
  );

  const bPopulationCapacityTotal = getMetricValue(
    METRIC_KEY.bPopulationCapacityTotal,
    metricValues,
    metricValueLevel
  );

  if (
    mBuildingAreaDemandExpected !== undefined &&
    oBuildingAreaDemandComparison !== undefined &&
    rPopulationNewZonesDemand !== undefined &&
    bPopulationCapacityTotal !== undefined
  ) {
    value = formulas.compensation(
      mBuildingAreaDemandExpected.value,
      oBuildingAreaDemandComparison.value,
      rPopulationNewZonesDemand.value,
      bPopulationCapacityTotal.value
    );
  } else {
    messages.push({
      message: getMissingInputsMessage(
        METRIC_KEY.compensation,
        metricValueLevel
      ),
      severity: "error",
    });
  }

  return {
    value: value,
    context: new MetricValueContext(METRIC_KEY.compensation, metricValueLevel),
    messages: messages,
  };
};

/**
 * Gets a metric value by starting on the given metric value level and moving
 * up to a higher level if no metric value is found.
 */
function getMetricValue(
  metricKey: string,
  metricValues: MetricValues,
  metricValueLevel: MetricValueLevel
): MetricValue | undefined {
  let metricValueContext: MetricValueContext;

  switch (metricValueLevel.name) {
    case "districtZoneGde":
      metricValueContext = new MetricValueContext(metricKey, metricValueLevel);

      if (metricValues.getValue(metricValueContext) === undefined) {
        metricValueContext = new MetricValueContext(metricKey, {
          name: "district",
          bfsNr: metricValueLevel.bfsNr,
          bfsNr1999: metricValueLevel.bfsNr1999,
        });

        if (metricValues.getValue(metricValueContext) === undefined) {
          metricValueContext = new MetricValueContext(metricKey, {
            name: "municipality",
            bfsNr: metricValueLevel.bfsNr,
          });

          if (metricValues.getValue(metricValueContext) === undefined) {
            metricValueContext = new MetricValueContext(metricKey, {
              name: "canton",
            });
          }
        }
      }
      break;

    case "districtResidential":
      metricValueContext = new MetricValueContext(metricKey, metricValueLevel);

      if (metricValues.getValue(metricValueContext) === undefined) {
        metricValueContext = new MetricValueContext(metricKey, {
          name: "municipality",
          bfsNr: metricValueLevel.bfsNr,
        });

        if (metricValues.getValue(metricValueContext) === undefined) {
          metricValueContext = new MetricValueContext(metricKey, {
            name: "canton",
          });
        }
      }
      break;

    case "district":
      metricValueContext = new MetricValueContext(metricKey, metricValueLevel);

      if (metricValues.getValue(metricValueContext) === undefined) {
        metricValueContext = new MetricValueContext(metricKey, {
          name: "municipality",
          bfsNr: metricValueLevel.bfsNr,
        });

        if (metricValues.getValue(metricValueContext) === undefined) {
          metricValueContext = new MetricValueContext(metricKey, {
            name: "canton",
          });
        }
      }
      break;

    case "zonTypPbg":
      metricValueContext = new MetricValueContext(metricKey, metricValueLevel);
      if (metricValues.getValue(metricValueContext) === undefined) {
        metricValueContext = new MetricValueContext(metricKey, {
          name: "canton",
        });
      }
      break;

    case "municipality":
      metricValueContext = new MetricValueContext(metricKey, metricValueLevel);

      if (metricValues.getValue(metricValueContext) === undefined) {
        metricValueContext = new MetricValueContext(metricKey, {
          name: "canton",
        });
      }
      break;
    case "canton":
      metricValueContext = new MetricValueContext(metricKey, metricValueLevel);
      break;
  }

  return metricValues.getValue(metricValueContext);
}

/**
 * Gets a parameter value with a parameter value level which corresponds to the given
 * metric value level. If no parameter value is found on the current parameter value level,
 * the next higher level is tried.
 */
function getParameterValue(
  parameterKey: string,
  parameterValues: ParameterValues,
  metricValueLevel: MetricValueLevel
): ParameterValue | undefined {
  let parameterValueContext;

  switch (metricValueLevel.name) {
    case "districtZoneGde":
      parameterValueContext = new ParameterValueContext(parameterKey, {
        name: "districtZoneGde",
        bfsNr1999: metricValueLevel.bfsNr1999,
        zonTypGde: metricValueLevel.zonTypGde,
      });

      if (parameterValues.getValue(parameterValueContext) === undefined) {
        parameterValueContext = new ParameterValueContext(parameterKey, {
          name: "district",
          bfsNr1999: metricValueLevel.bfsNr1999,
        });

        if (parameterValues.getValue(parameterValueContext) === undefined) {
          parameterValueContext = new ParameterValueContext(parameterKey, {
            name: "canton",
          });
        }
      }

      break;

    case "districtResidential":
      parameterValueContext = new ParameterValueContext(parameterKey, {
        name: "district",
        bfsNr1999: metricValueLevel.bfsNr1999,
      });

      if (parameterValues.getValue(parameterValueContext) === undefined) {
        parameterValueContext = new ParameterValueContext(parameterKey, {
          name: "canton",
        });
      }

      break;

    case "district":
      parameterValueContext = new ParameterValueContext(parameterKey, {
        name: "district",
        bfsNr1999: metricValueLevel.bfsNr1999,
      });

      if (parameterValues.getValue(parameterValueContext) === undefined) {
        parameterValueContext = new ParameterValueContext(parameterKey, {
          name: "canton",
        });
      }

      break;

    case "zonTypPbg":
      parameterValueContext = new ParameterValueContext(parameterKey, {
        name: "canton",
      });

      break;

    case "municipality":
      parameterValueContext = new ParameterValueContext(parameterKey, {
        name: "canton",
      });

      break;

    case "canton":
      parameterValueContext = new ParameterValueContext(parameterKey, {
        name: "canton",
      });

      break;
  }

  return parameterValues.getValue(parameterValueContext);
}

function getMissingInputsMessage(
  metricOrParameterKey: string,
  level: MetricValueLevel
) {
  return `Fehlende Inputdaten für ${metricOrParameterKey} auf Stufe ${getLevelText(
    level
  )}`;
}

function getSkipPbgCalculationMessage(
  metricKey: string,
  level: MetricValueLevel
) {
  return `Überspringe Kennzahl ${metricKey} für Gemeindezone ${
    (level as any).zonTypGde
  }, Zone PBG > 3000. Benutze Wert 0.`;
}

function getCalculateWithUezMessage(
  metricKey: string,
  level: MetricValueLevel
) {
  return `Berechnung ${metricKey} auf Stufe ${getLevelText(level)} mit UEZ`;
}

function getCalculateWithAzMessage(metricKey: string, level: MetricValueLevel) {
  return `Berechnung ${metricKey} auf Stufe ${getLevelText(level)} mit AZ`;
}

function getLevelText(level: MetricValueLevel): string {
  let levelText = "";
  switch (level.name) {
    case "districtZoneGde":
      levelText = `Ortsteil: ${level.bfsNr1999}, Gemeindezone: ${level.zonTypGde}`;
      break;
    case "zonTypPbg":
      levelText = `Zonentyp PBG: ${level.zonTypPbg}`;
      break;
    case "districtResidential":
      levelText = `Ortsteil: ${level.bfsNr1999}`;
      break;
    case "district":
      levelText = `Ortsteil: ${level.bfsNr1999}`;
      break;
    case "municipality":
      levelText = `Gemeinde: ${level.bfsNr}`;
      break;
    case "canton":
      levelText = "Kanton";
      break;
  }

  return levelText;
}
